import React, {useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import PrimaryButton from '../components/ui/PrimaryButton';
import VideoCard from '../components/ui/VideoCard';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiCheck,
  FiEye,
  FiMonitor,
  FiPhone,
  FiPlus,
  FiStar,
  FiTarget,
  FiTrendingUp,
  FiUser,
  FiZap
} = FiIcons;

const EXTERNAL_CHECKOUT_URL = 'https://app.paythen.co/company/elite/plan/didi43uyi5';
const PIPELINE_CHECKOUT_URL = 'https://app.paythen.co/company/elite/plan/iafb3zbjpt';
const CLIENT_PORTAL_URL = 'https://pinchat.app/elite';
const DEMO_PLATFORM_URL = 'https://splendid-zuccutto-80ca0b.netlify.app';
const VIDEO_IDS = ['zIlhwxC7j2U', '7d7ZfQDM38Y', '9ELoQ5hSSfQ', 'au5rlDs21Zc', 'j0JKCKFOgf0'];

const plans = [
  {
    title: 'Build Your Pipeline',
    price: '$159',
    detail: 'Custom lead system plus a professional follow-up call within 24 hours.',
    features: ['Custom lead generation website', 'Lead capture system', 'Hot appointments to your phone', 'Cancel anytime']
  },
  {
    title: 'Pipeline King',
    price: '$259',
    detail: 'A full 30-day multi-touch follow-up campaign for every incoming lead.',
    features: ['Everything in the base plan', 'Phone, text, and voicemail', '30-day lead nurturing', 'Priority support']
  }
];

const testimonials = [
  {
    text: 'I had 12 appointments booked from my leads in the first month and closed four deals that would never have walked on the lot.',
    name: 'Marcus T.',
    title: 'Honda Sales Professional, Texas'
  },
  {
    text: 'One deal from the system pays for it twice over. I am averaging three to four deals monthly from leads I would never have seen.',
    name: 'Jennifer R.',
    title: 'Toyota Sales Consultant, Florida'
  }
];

function TopBanner() {
  return (
    <div className="overflow-hidden bg-yellow-400 py-2 text-center text-[10px] font-black uppercase tracking-widest text-black md:text-xs">
      No dealership approval needed · Build your own pipeline · 30-day guarantee · Starting at $159/mo
    </div>
  );
}

function NavBar({onChange, onCheckout}) {
  return (
    <nav className="sticky top-0 z-50 flex items-center justify-between border-b border-slate-800 bg-[#0B1320] px-4 py-4 md:px-8">
      <button className="flex items-center gap-3" onClick={() => onChange('main')}>
        <span className="flex h-8 w-8 items-center justify-center rounded-full border border-yellow-400">
          <SafeIcon icon={FiTrendingUp} className="text-sm text-yellow-400" />
        </span>
        <span className="text-lg font-black tracking-widest text-white">ELITE SALES</span>
      </button>
      <div className="hidden gap-7 lg:flex">
        {['demo', 'proof', 'results', 'plans'].map((item) => (
          <button
            key={item}
            onClick={() => onChange(item)}
            className="text-xs font-bold uppercase tracking-widest text-slate-400 transition hover:text-white"
          >
            {item}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={() => {
            window.location.href = CLIENT_PORTAL_URL;
          }}
          className="hidden items-center gap-2 border border-slate-700 px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-300 sm:flex"
        >
          <SafeIcon icon={FiUser} />
          Client Portal
        </button>
        <PrimaryButton className="px-4 py-2 text-xs" onClick={onCheckout}>
          Build My Pipeline
        </PrimaryButton>
      </div>
    </nav>
  );
}

function MainSection({onCheckout, onChange}) {
  return (
    <div className="mx-auto flex max-w-6xl flex-col items-center px-4 pb-16 pt-16 text-center md:pt-24">
      <p className="mb-8 rounded-full border border-yellow-400/30 bg-yellow-400/5 px-5 py-2 text-xs font-bold uppercase tracking-[0.2em] text-yellow-400">
        No Dealership Approval Needed
      </p>
      <h1 className="text-4xl font-black uppercase leading-[0.9] text-white md:text-7xl">
        Get New Car Buyers<br />
        <span className="text-yellow-400">Hunting You Down</span><br />
        Every Month
      </h1>
      <p className="mt-8 max-w-3xl text-xl font-medium leading-relaxed text-slate-400">
        We build your personal lead machine. Our call center follows up with every lead so you can show up and close.
      </p>
      <div className="mt-12 grid w-full gap-6 md:grid-cols-3">
        {[
          {icon: FiTarget, title: 'We Build Your Website', text: 'Your name, face, number, and a high-converting lead capture page.'},
          {icon: FiZap, title: 'You Drive Buyers To It', text: 'Bring local, payment-ready buyers directly into your personal pipeline.'},
          {icon: FiPhone, title: 'We Follow Up Fast', text: 'Our trained team works each lead while you focus on closing.'}
        ].map((item) => (
          <div key={item.title} className="rounded-xl border border-slate-700/50 bg-[#131C2F] p-7">
            <span className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-yellow-400">
              <SafeIcon icon={item.icon} className="text-xl text-black" />
            </span>
            <h2 className="text-lg font-black text-white">{item.title}</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">{item.text}</p>
          </div>
        ))}
      </div>
      <div className="mt-12 flex flex-col gap-4 sm:flex-row">
        <PrimaryButton className="px-8 py-4 text-lg" onClick={onCheckout}>
          Start Building My Pipeline
        </PrimaryButton>
        <button
          onClick={() => onChange('demo')}
          className="border border-slate-600 px-8 py-4 text-sm font-bold uppercase tracking-wide text-white transition hover:border-yellow-400"
        >
          See Live Demo
        </button>
      </div>
    </div>
  );
}

function PricingSection({onCheckout}) {
  return (
    <div className="mx-auto max-w-6xl px-4 py-16">
      <div className="mb-12 text-center">
        <h2 className="text-4xl font-black uppercase text-white">Choose Your Pipeline</h2>
        <p className="mt-4 text-lg text-slate-400">Get your custom lead generation system built for you.</p>
      </div>
      <div className="grid gap-8 md:grid-cols-2">
        {plans.map((plan, index) => (
          <div key={plan.title} className="rounded-2xl border border-slate-700 bg-[#131C2F] p-8 shadow-2xl">
            <h3 className="text-2xl font-black text-white">{plan.title}</h3>
            <p className="mt-4 text-5xl font-black text-yellow-400">{plan.price}<span className="text-lg text-slate-400">/mo</span></p>
            <p className="mt-5 text-slate-300">{plan.detail}</p>
            <ul className="my-8 space-y-4">
              {plan.features.map((feature) => (
                <li key={feature} className="flex gap-3 text-sm text-slate-300">
                  <SafeIcon icon={FiCheck} className="mt-0.5 shrink-0 text-yellow-400" />
                  {feature}
                </li>
              ))}
            </ul>
            <PrimaryButton
              fullWidth
              className="py-4 text-base"
              onClick={() => {
                window.location.href = index === 0 ? EXTERNAL_CHECKOUT_URL : PIPELINE_CHECKOUT_URL;
              }}
            >
              Lock In This Plan
            </PrimaryButton>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProofSection() {
  const [visible, setVisible] = useState(3);
  return (
    <div className="mx-auto max-w-6xl px-4 py-16">
      <div className="mb-12 text-center">
        <h2 className="text-4xl font-black uppercase text-white">The Proof</h2>
        <p className="mt-4 text-lg text-slate-400">Hear directly from the closers building their own pipelines.</p>
      </div>
      <div className="grid gap-6 md:grid-cols-3">
        {VIDEO_IDS.slice(0, visible).map((id, index) => <VideoCard key={id} id={id} index={index} />)}
      </div>
      {visible < VIDEO_IDS.length && (
        <button
          onClick={() => setVisible((count) => Math.min(count + 3, VIDEO_IDS.length))}
          className="mx-auto mt-10 flex items-center gap-2 rounded-xl border border-slate-700 bg-[#131C2F] px-8 py-4 text-sm font-bold uppercase tracking-widest text-white"
        >
          <SafeIcon icon={FiPlus} />
          Load More Proof
        </button>
      )}
    </div>
  );
}

function ResultsSection({onCheckout}) {
  return (
    <div className="mx-auto max-w-6xl px-4 py-16">
      <div className="mb-12 text-center">
        <h2 className="text-4xl font-black uppercase text-white">Real Results From Real Closers</h2>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        {testimonials.map((testimonial) => (
          <div key={testimonial.name} className="rounded-2xl border border-slate-700/50 bg-[#131C2F] p-8">
            <div className="mb-6 flex gap-1">
              {[0, 1, 2, 3, 4].map((star) => <SafeIcon key={star} icon={FiStar} className="fill-yellow-400 text-sm text-yellow-400" />)}
            </div>
            <blockquote className="text-lg italic leading-relaxed text-slate-300">“{testimonial.text}”</blockquote>
            <p className="mt-6 border-t border-slate-700 pt-4 font-black text-white">{testimonial.name}</p>
            <p className="mt-1 text-xs font-bold uppercase tracking-widest text-slate-500">{testimonial.title}</p>
          </div>
        ))}
      </div>
      <PrimaryButton className="mx-auto mt-12 px-10 py-4 text-lg" onClick={onCheckout}>
        Build My Pipeline Now
      </PrimaryButton>
    </div>
  );
}

function DemoSection({onCheckout}) {
  return (
    <div className="mx-auto max-w-4xl px-4 py-16 text-center">
      <SafeIcon icon={FiMonitor} className="mx-auto text-5xl text-yellow-400" />
      <h2 className="mt-6 text-4xl font-black uppercase text-white">See The Platform In Action</h2>
      <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-400">
        Explore exactly how your dashboard tracks leads, follow-ups, and appointments.
      </p>
      <PrimaryButton
        className="mx-auto mt-10 px-9 py-4 text-lg"
        onClick={() => window.open(DEMO_PLATFORM_URL, '_blank', 'noopener,noreferrer')}
      >
        <SafeIcon icon={FiEye} />
        Explore Live Demo
      </PrimaryButton>
      <PrimaryButton className="mx-auto mt-5 px-9 py-4 text-lg" onClick={onCheckout}>
        Start Building My Pipeline
      </PrimaryButton>
    </div>
  );
}

function SalesPage() {
  const [section, setSection] = useState('main');
  const checkout = () => {
    window.location.href = EXTERNAL_CHECKOUT_URL;
  };

  const sections = {
    main: <MainSection onCheckout={checkout} onChange={setSection} />,
    demo: <DemoSection onCheckout={checkout} />,
    proof: <ProofSection />,
    results: <ResultsSection onCheckout={checkout} />,
    plans: <PricingSection onCheckout={checkout} />
  };

  return (
    <div className="min-h-screen bg-[#0B1320] font-sans text-white">
      <TopBanner />
      <NavBar onChange={setSection} onCheckout={checkout} />
      <AnimatePresence mode="wait">
        <motion.main
          key={section}
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          className="min-h-[70vh]"
        >
          {sections[section]}
        </motion.main>
      </AnimatePresence>
    </div>
  );
}

export default SalesPage;